import { t } from '@lingui/macro';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { motion } from 'motion/react';
import { useContext, useState } from 'react';
import { Spinner } from 'react-activity';
import { toast } from 'sonner';

import { AppButton } from '@/UI';
import { encryptDataWithRSA } from '@/Utils/cryptoUtils';
import {
    AddUserToP2PWhiteList,
    rejectUserFromP2PWhiteList,
} from '@/Utils/socket-actions/p2p-connection/p2pApis';
import { sendP2PSocketData } from '@/Utils/socket-actions/p2p-connection/p2pConnection';
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux';
import { p2pActions } from '@/store/p2p-slice';

import classes from './P2PAdminPanel.module.css';
import { P2PContext } from './P2PContext';
import { generateUserSign } from './P2PHelper';

dayjs.extend(utc);

interface IProps {}

const P2PWaitingUserDetails: React.FC<IProps> = () => {
    const utcISOString = dayjs().utc().toISOString();
    const dispatch = useAppDispatch();

    const context = useContext(P2PContext);
    const { setActiveUserItem, activeUserItem } = context;

    const [isSubmitting, setIsSubmitting] = useState(false);

    const serverDataSelector = useAppSelector(state => state.serverDataReducer);
    const activeServer = serverDataSelector?.activeServer;
    const publicKey = serverDataSelector?.serverData?.[activeServer]?.publicKey;

    const approveUser = async (reject: boolean = false) => {
        setIsSubmitting(true);

        const userHash = generateUserSign({
            uuid: activeUserItem?.device_id,
            userId: activeUserItem?.user_id,
        });

        const dateEncrypted = await encryptDataWithRSA(utcISOString, publicKey);
        const deviceIdEncrypted = await encryptDataWithRSA(
            activeUserItem?.device_id,
            publicKey,
        );
        const userHashEncrypted = await encryptDataWithRSA(userHash, publicKey);

        const body = {
            uuid: deviceIdEncrypted,
            hash: userHashEncrypted,
            date: dateEncrypted,
            user_id: activeUserItem?.user_id,
            subserver: activeServer,
        };
        const func = reject
            ? rejectUserFromP2PWhiteList
            : AddUserToP2PWhiteList;
        const successMsg = reject
            ? t`User added to the rejected`
            : t`User added to the permitted whitelist`;

        const errorMsg = reject
            ? t`Error while rejecting user from the whitelist. Please try again.`
            : t`Error adding user to the permitted whitelist. Please try again.`;
            
        try {
            const { error, response = undefined } = await func(body);

            if (response && !error) {
                dispatch(
                    p2pActions.removeWaitingUser(activeUserItem?.device_id),
                );
                setActiveUserItem(null);
                toast.success(successMsg);
                sendP2PSocketData({
                    command: 'broadcast',
                    data: {
                        event: 'whitelist_user_added',
                        user_id: activeUserItem?.user_id,
                        device_id: activeUserItem?.device_id,
                    },
                });
            } else {
                toast.error(response || errorMsg);
            }
        } catch (err) {
            console.error(err);
            toast.error(errorMsg);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, x: 10 }}
            exit={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            className={`${classes['body']}`}>
            <table>
                <tbody>
                    <tr>
                        <td>Name: </td>
                        <td>{activeUserItem?.name}</td>
                    </tr>
                    <tr>
                        <td>Username: </td>
                        <td>@{activeUserItem?.username}</td>
                    </tr>
                    <tr>
                        <td>User ID: </td>
                        <td>{activeUserItem?.user_id}</td>
                    </tr>
                    <tr>
                        <td>Is user a member: </td>
                        <td>
                            {activeUserItem?.is_outside_network ? 'No' : 'Yes'}
                        </td>
                    </tr>
                    <tr>
                        <td>device Id: </td>
                        <td>{activeUserItem?.device_id || 'N/A'}</td>
                    </tr>
                </tbody>
            </table>
            <div
                style={{
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'center',
                    justifyContent: 'start',
                }}>
                <motion.div className={classes['flex']}>
                    <AppButton
                        disabled={isSubmitting}
                        onClick={() => approveUser(false)}
                        title={'Approve'}
                        classStyles={`${classes['details-btn']} ${classes['revoke-btn']}`}
                    />
                </motion.div>
                <motion.div className={classes['flex']}>
                    <AppButton
                        disabled={isSubmitting}
                        buttonType={'secondary'}
                        onClick={() => approveUser(true)}
                        title={'Reject'}
                        classStyles={`${classes['details-btn']} ${classes['revoke-btn']}`}
                    />
                </motion.div>
            </div>
        </motion.div>
    );
};
export default P2PWaitingUserDetails;
